import {
  useCreate,
  useCreateSuggestionContext,
  useNotify,
  SimpleForm,
  TextInput,
  Create,
  NumberInput,
  useResourceContext,
  required,
  number,
  maxLength,
  useRefresh,
  AutocompleteInput,
  BooleanInput,
  minLength,
  minValue,
  useTranslate,
  ReferenceInput,
} from "react-admin";
import { useFormContext } from "react-hook-form";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { useCallback, useEffect, useState } from "react";
import MaxStock from "../../products/common/MaxStock";
import {
  OptionRendererKind,
  filterToQueryKind,
  inputTextKind,
  matchSuggestionKind,
} from "../../common/OptionRendererKind";
import DisabledInput from "../../products/common/DisabledInput";
import WarehouseReference from "../../products/common/WarehouseReference";
import SaveDeleteBtns from "../../saveDeleteBtns/SaveDeleteBtn";

const greaterThan = (value, allValues) => {
  if (value < allValues.min_stock) {
    return "Must be greater than " + allValues.min_stock;
  }
  return undefined;
};
const lessThan = (value, allValues) => {
  if (value > allValues.max_stock) {
    return "Must be less than " + allValues.max_stock;
  }
  return undefined;
};

const validateWarehouse = [required(), minLength(36), maxLength(36)];
const validateKind = [required(), minLength(36), maxLength(36)];
const validateMinStock = [number(), minValue(0), lessThan];
const validateMaxStock = [number(), minValue(0), greaterThan];
const validateActive = [required()];
const validateNote = [maxLength(125)];
const validateUnit = [maxLength(100)];

const CreateNewProduct = () => {
  const t = useTranslate();
  const notify = useNotify();
  const { filter, onCancel, onCreate } = useCreateSuggestionContext();
  const [create] = useCreate();

  const save = useCallback(
    async (values) => {
      try {
        await create(
          "products",
          { data: values },
          {
            returnPromise: true,
            onSuccess: (data) => {
              notify("ra.notification.created", {
                messageArgs: { smart_count: 1 },
              });
              onCreate(data);
              //   setOpen(false);
              //   refresh();
            },
          }
        );
      } catch (error) {
        if (error.body && error.body.errors) {
          // The shape of the returned validation errors must match the shape of the form
          return error.body.errors;
        }
      }
    },
    [create]
  );

  return (
    <Dialog open={true} onClose={onCancel}>
      <Create title={" "} sx={{ "& .RaCreate-main": { mt: 0 } }}>
        <SimpleForm
          warnWhenUnsavedChanges
          onSubmit={save}
          toolbar={<SaveDeleteBtns />}
          mode="onBlur"
          reValidateMode="onBlur"
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pt: 0,
            }}
          >
            <DialogTitle>{t("mine.dialog.create_product")}</DialogTitle>
            <IconButton onClick={onCancel} sx={{ mr: "11px" }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <DialogContent>
            <ReferenceInput
              source="kind_id"
              reference="kinds"
              perPage={10}
              sort={{ field: "kinds.created_at", order: "ASC" }}
              // filter={{ all: "1102" }}
            >
              <AutocompleteInput
                label="mine.label.kind"
                variant="outlined"
                fullWidth
                sx={{ display: "inline-flex" }}
                validate={validateKind}
                optionValue="id"
                // optionText="code"
                optionText={<OptionRendererKind />}
                inputText={inputTextKind}
                matchSuggestion={matchSuggestionKind}
                filterToQuery={filterToQueryKind}
              />
            </ReferenceInput>

            <DisabledInput dependOn="kind_id" codeDoesNotExist={filter.split(": ")[1]} />

            <WarehouseReference
              variant="outlined"
              source="warehouse_id"
              label="mine.label.warehouse"
              fullWidth
              validate={validateWarehouse}
              reference="warehouses"
              orderBy="created_at"
              searchBy="name"
            />

            <TextInput
              variant="outlined"
              source="unit"
              label="mine.label.unit"
              fullWidth
              validate={validateUnit}
            />

            <Grid
              container
              spacing={2}
              sx={{
                "& .MuiGrid-item": { pt: 0 },
                mt: 0,
              }}
            >
              <Grid item xs={12} lg={6}>
                <NumberInput
                  variant="outlined"
                  source="min_stock"
                  fullWidth
                  validate={validateMinStock}
                />
              </Grid>
              <Grid item xs={12} lg={6}>
                <MaxStock
                  variant="outlined"
                  source="max_stock"
                  fullWidth
                  validate={validateMaxStock}
                  dependOn="min_stock"
                />
              </Grid>
            </Grid>

            <TextInput
              variant="outlined"
              source="note"
              label="mine.label.note"
              fullWidth
              multiline
              minRows={3}
              maxRows={3}
              validate={validateNote}
            />

            <BooleanInput
              variant="outlined"
              source="active"
              fullWidth
              isRequired
              validate={validateActive}
              defaultValue={true}
            />
          </DialogContent>
        </SimpleForm>
      </Create>
    </Dialog>
  );
};

export default CreateNewProduct;
